import React, { useEffect, useState } from 'react';
import LeaderboardBanner from '@components/leaderboard/LeaderboardBanner';
import { LeaderboardTableItemProps } from '@components/leaderboard/list/LeaderboardTableItem';
import { useCurrentLeaderboard } from '@hooks/leaderboard.hook';
import moment from 'moment';
import FillHeightContainer from '@components/misc/FillHeightContainer';
import { LoadingScroll } from 'geniebook-ui/dist';
import { customHumanizeDuration } from '@utils/helpers/momentHelper';
import LeaderboardTable from '@components/leaderboard/list/LeaderboardTable';
import LeaderboardEmptyState from '@components/leaderboard/LeaderboardEmptyState';
import { DetailUser } from '@models/types/users.type';
import { useAtomValue } from 'jotai';
import {
  haveSubjectForTrialAtom,
  userDetailQueryAtom,
} from '@atom/globalQueryAtom';
import LeaderboardUpsellingBanner from '@components/leaderboard/LeaderboardUpsellingBanner';

const Leaderboard = () => {
  const [seasonEndTime, setSeasonEndTime] = useState('');
  const [showMore, setShowMore] = useState(false);
  const currentLeaderboardQuery = useCurrentLeaderboard();
  const userDetailQuery = useAtomValue(userDetailQueryAtom);
  const [leaderboardList, setLeaderboardList] = useState<
    LeaderboardTableItemProps[]
  >([]);
  const [topLeaderboard, setTopLeaderboard] = useState<
    LeaderboardTableItemProps[]
  >([]);
  const [bottomLeaderboard, setBottomLeaderboard] = useState<
    LeaderboardTableItemProps[]
  >([]);
  const tier: number = currentLeaderboardQuery.data?.data?.tier ?? 0;
  const haveSubjectForTrial = useAtomValue(haveSubjectForTrialAtom);

  useEffect(() => {
    if (currentLeaderboardQuery.data?.data) {
      const currentLeaderboard = currentLeaderboardQuery.data.data.current_data;
      const leaderboard: LeaderboardTableItemProps[] = [];
      let standing = 1;
      currentLeaderboard.forEach(item => {
        leaderboard.push({
          standing: standing,
          bubble: item.total_score,
          name: [item.name, item.last_name].join(' '),
          profilePicture: item.profile_picture,
          student_id: item.student_id,
        });
        standing++;
      });
      setLeaderboardList(leaderboard);
    }
  }, [currentLeaderboardQuery.data]);

  useEffect(() => {
    if (
      userDetailQuery.data?.data &&
      leaderboardList.length > 0 &&
      topLeaderboard.length == 0
    ) {
      fillTopAndBottomLeaderboard(leaderboardList, userDetailQuery.data.data);
    }
  }, [userDetailQuery.data, leaderboardList]);

  function calculateEndTime() {
    const now = moment();
    let endOfSeason = moment(
      moment().endOf('month').format('YYYY-MM-DD HH:mm:ss') + '+08:00',
    );
    if (now.isAfter(endOfSeason)) {
      endOfSeason = moment(
        moment()
          .endOf('month')
          .add(1, 'day')
          .endOf('month')
          .format('YYYY-MM-DD HH:mm:ss') + '+08:00',
      );
    }

    const duration = moment.duration(endOfSeason.diff(now));

    setSeasonEndTime(customHumanizeDuration(duration));
  }

  useEffect(() => {
    calculateEndTime();
    // calculate end season every ten second
    const calculationInterval = setInterval(calculateEndTime, 10000);
    return () => {
      clearInterval(calculationInterval);
    };
  }, []);

  function fillTopAndBottomLeaderboard(
    leaderboardList: LeaderboardTableItemProps[],
    user: DetailUser,
  ) {
    const topLeaderboardNew: LeaderboardTableItemProps[] = [];
    const bottomLeaderboardNew: LeaderboardTableItemProps[] = [];
    const newList: LeaderboardTableItemProps[] = leaderboardList;

    let indexUser = null;
    for (let i = 0; i < newList.length; i++) {
      const item = { ...newList[i] };
      if (item.student_id != parseInt(user.user_id)) {
        continue;
      }

      indexUser = i;
      item.isCurrentUser = true;
      const previousDayData =
        currentLeaderboardQuery.data?.data?.previous_day_calculation;
      if (previousDayData) {
        item.lastStanding =
          previousDayData.findIndex(
            standing => standing.student_id == parseInt(user.user_id),
          ) + 1;
      }
      newList.splice(i, 1, item);
      break;
    }

    if (indexUser == null || indexUser < 9) {
      topLeaderboardNew.push(...newList.slice(0, 9));
    } else if (indexUser == 9) {
      topLeaderboardNew.push(...newList.slice(0, 7));
      bottomLeaderboardNew.push(...newList.slice(9, 12));
    } else if (indexUser >= newList.length - 5) {
      topLeaderboardNew.push(...newList.slice(0, 5));
      bottomLeaderboardNew.push(
        ...newList.slice(newList.length - 5, newList.length),
      );
    } else {
      topLeaderboardNew.push(...newList.slice(0, 5));
      bottomLeaderboardNew.push(...newList.slice(indexUser, indexUser + 5));
    }

    setTopLeaderboard(topLeaderboardNew);
    setBottomLeaderboard(bottomLeaderboardNew);
    setLeaderboardList(newList);
  }

  function handleShowMore() {
    setBottomLeaderboard([]);
    setTopLeaderboard(leaderboardList);
    setShowMore(true);
  }

  return (
    <FillHeightContainer className={'overflow-auto no-scrollbar'}>
      <div className={'flex justify-between mb-8 max-lg:hidden'}>
        <div className={'text-neutral-whiteboard text-lg font-semibold'}>
          Leaderboard
        </div>
        <div className={'text-primary-skyblue text-md font-medium'}>
          {leaderboardList.length > 0
            ? 'Season ends in ' + seasonEndTime
            : 'Season starting soon'}
        </div>
      </div>
      {leaderboardList.length > 0 ? (
        <div className={'max-lg:mb-4 lg:mb-6 md:max-lg:mt-4'}>
          <LeaderboardBanner tier={tier} />
        </div>
      ) : null}

      <div
        className={
          'text-primary-skyblue text-center text-sm font-bold lg:hidden mb-4'
        }
      >
        {leaderboardList.length > 0
          ? 'Season ends in ' + seasonEndTime
          : 'Season starting soon'}
      </div>

      {leaderboardList.length > 0 ? (
        <LeaderboardTable
          topLeaderboard={topLeaderboard}
          leaderboardList={leaderboardList}
          bottomLeaderboard={bottomLeaderboard}
          showMore={showMore}
          onShowMore={handleShowMore}
        />
      ) : (
        <LeaderboardEmptyState />
      )}

      {currentLeaderboardQuery.isLoading || userDetailQuery.isLoading ? (
        <LoadingScroll isBlockOthers={true} />
      ) : null}
      {haveSubjectForTrial && <LeaderboardUpsellingBanner />}
    </FillHeightContainer>
  );
};

export default Leaderboard;
